import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CRM_URL } from "../design/AccountMenu";
import { feedbackApi, isCrmAuthError } from "./api";
import { FAB_DRAG_THRESHOLD, FAB_SIZE, fabPosFromDrop, loadFabPos, saveFabPos, type FabPos } from "./fabPos";
import { FeedbackForm, type SentInfo } from "./FeedbackForm";
import { MineDetail, MineList } from "./FeedbackMine";
import {
  IconArrowLeft,
  IconBug,
  IconCircleHelp,
  IconClipboardList,
  IconExternalLink,
  IconLightbulb,
  IconMaximize,
  IconMinimize,
  IconPlus,
  IconX,
} from "./icons";
import { feedbackGate, type FeedbackKind } from "./model";
import { sentDueText } from "./supportFormat";
import { CrmLoginNeeded, Pill } from "./ui";
import "./feedback.css";

/**
 * Плавающий «+» в правом нижнем углу SMS-центра — общий с CRM и FISHKA HUB (владелец 19.10.2026:
 * «один общий плюсик, устроен как в CRM»; образец — frontend/src/components/feedback/FeedbackWidget.tsx).
 * Оранжевая кнопка 52 px, «+» поворачивается в «×»; клик раскрывает стопку белых плашек, форма
 * открывается шторкой 400 px (можно развернуть на весь экран, на телефоне — всегда на весь).
 *
 * Плашки: «Вопрос в УК» (если CRM его не выключила: `questionEnabled`) · «Сообщить об ошибке» ·
 * «Предложить идею» · «Мои обращения». Партнёру (франчайзи) «Мои обращения» открывают CRM → кабинет
 * партнёра (новая вкладка), остальным — список здесь.
 *
 * Обращения — это CRM, а не SMS-центр: запросы идут в /api/feedback*, Go (go/retention/
 * crm_feedback_proxy.go) пробрасывает их на CRM вместе с её cookie. Если пользователь пришёл по
 * SSO-токену и cookie CRM на sms-домене нет, CRM ответит 401 — вместо формы/списка показываем
 * «Войдите в CRM, чтобы отправить обращение» (JWT SMS-центра при этом не трогаем).
 *
 * Кнопку можно перетащить мышью или пальцем к левому или правому краю — место запоминается
 * (fabPos.ts); двойной клик возвращает её в угол. Монтирует AppShell — только для вошедшего.
 */

type View = "menu" | FeedbackKind | "mine" | "detail" | "sent";

/** Кабинет партнёра в CRM, вкладка «Обращения». */
export const CRM_REQUESTS_URL = `${CRM_URL}partner/dashboard?tab=requests`;

const DEFAULT_TEXTS = {
  bugIntro: "Что-то не работает или показывает неверно",
  ideaIntro: "Идея для SMS-центра: что добавить или изменить",
  slaText: "Ответим в течение рабочего дня",
};

const TITLES: Record<Exclude<View, "menu">, string> = {
  question: "Вопрос в управляющую компанию",
  bug: "Сообщить об ошибке",
  idea: "Предложить идею",
  mine: "Мои обращения",
  detail: "Обращение",
  sent: "Спасибо!",
};

/** Телефон: форма всегда на весь экран (брейкпоинт плотной раскладки SMS-центра). */
const NARROW_QUERY = "(max-width: 560px)";

/** «Домашняя полоска» iPhone в пикселях — меряем невидимым замером, env() из JS не прочитать. */
function safeBottom(probe: HTMLElement | null): number {
  if (!probe) return 0;
  return parseFloat(getComputedStyle(probe).height) || 0;
}

/** Сколько сверху занимает шапка (+ небольшой зазор): выше неё кнопку не ставим. */
function headerReserve(): number {
  const el = document.querySelector(".fk-topbar");
  return (el ? el.getBoundingClientRect().height : 56) + 8;
}

function Plate({
  icon,
  tone,
  onClick,
  badge,
  children,
}: {
  icon: ReactNode;
  tone: string;
  onClick: () => void;
  badge?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <button type="button" role="menuitem" className="fk-fb-plate" onClick={onClick}>
      <span className="fk-fb-plate-ic" style={{ color: tone }}>
        {icon}
      </span>
      <span className="fk-fb-plate-label">{children}</span>
      {badge}
    </button>
  );
}

function IconBtn({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button type="button" className="fk-fb-iconbtn" title={title} aria-label={title} onClick={onClick}>
      {children}
    </button>
  );
}

/** Склонение: 1 ответ · 2 ответа · 5 ответов. */
function plural(n: number, forms: [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return forms[0];
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return forms[1];
  return forms[2];
}

export function FeedbackFab({
  franchisee,
  roleLabel,
}: {
  /** Партнёр (кабинет партнёра): «Мои обращения» открывают CRM. */
  franchisee: boolean;
  roleLabel: string | null;
}): JSX.Element | null {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("menu");
  const [expanded, setExpanded] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [sent, setSent] = useState<SentInfo | null>(null);
  // CRM ответила 401 на отправку / список: нет входа в CRM.
  const [authLost, setAuthLost] = useState(false);
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia(NARROW_QUERY).matches,
  );
  const [pos, setPos] = useState<FabPos | null>(() =>
    typeof window !== "undefined" ? loadFabPos() : null,
  );
  // Перетаскивание: точка нажатия; moved — чтобы отличить клик от переноса.
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const [dragAt, setDragAt] = useState<{ x: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const dockRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const safeProbe = useRef<HTMLSpanElement>(null);
  // Меню, открытое с клавиатуры, сразу получает фокус (стрелки, Enter); мышью — фокус остаётся на кнопке.
  const keyboardOpen = useRef(false);

  // Виджет CRM: включён ли, счётчики, тексты. Ошибка (в том числе 401) не прячет кнопку:
  // на 401 откроем «Войдите в CRM», на остальное — всё равно дадим попробовать.
  const widget = useQuery({
    queryKey: ["feedback", "widget"],
    queryFn: feedbackApi.widget,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const info = widget.data;
  const { feedbackOn, questionOn } = feedbackGate(info);
  const texts = info?.texts ?? DEFAULT_TEXTS;
  const counts = info?.counts;
  const badge = counts ? counts.verify + counts.unread : 0;
  const unauth = authLost || isCrmAuthError(widget.error);

  const side = pos?.side ?? "right";
  const full = expanded || narrow;

  // Свежий успешный ответ CRM — вход в CRM есть, прежний отказ (401 на отправке) больше не в силе.
  useEffect(() => {
    if (widget.dataUpdatedAt) setAuthLost(false);
  }, [widget.dataUpdatedAt]);

  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setExpanded(false);
    setView("menu");
  }, []);
  const onAuthLost = useCallback(() => setAuthLost(true), []);
  const retryAuth = () => {
    setAuthLost(false);
    void widget.refetch();
  };

  // Esc — свернуть с полного экрана, потом закрыть.
  useEffect(() => {
    if (!open) return;
    const h = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (expanded) setExpanded(false);
      else {
        close();
        btnRef.current?.focus();
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open, expanded, close]);

  // Стопка плашек закрывается кликом мимо (форму — нет: в ней может быть набранный текст).
  useEffect(() => {
    if (!open || view !== "menu") return;
    const h = (e: PointerEvent) => {
      if (e.target instanceof Node && !dockRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, [open, view]);

  // Меню, открытое с клавиатуры, — фокус на первую плашку.
  useEffect(() => {
    if (open && view === "menu" && keyboardOpen.current) {
      keyboardOpen.current = false;
      menuRef.current?.querySelector<HTMLElement>("button")?.focus();
    }
  }, [open, view]);

  // CRM выключила виджет роли — кнопки нет.
  if (!feedbackOn) return null;

  const go = (v: View) => {
    setView(v);
    setOpen(true);
  };

  // «Мои обращения»: партнёру — в кабинет партнёра CRM (новая вкладка), остальным — список здесь.
  const openMine = () => {
    if (franchisee) {
      close();
      window.open(CRM_REQUESTS_URL, "_blank", "noopener");
    } else go("mine");
  };

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>("button") ?? []);
    if (!items.length) return;
    e.preventDefault();
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === "ArrowDown" ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
    items[next].focus();
  };

  const subtitle = (v: Exclude<View, "menu">): string => {
    switch (v) {
      case "bug":
        return texts.bugIntro;
      case "idea":
        return texts.ideaIntro;
      case "question":
        return "Напишите своими словами — CRM сама поймёт, какой отдел ответит";
      case "mine":
        return "Статусы и ответы команды — здесь и в колокольчике CRM";
      default:
        return "";
    }
  };

  // Пока тащим — кнопка под пальцем; иначе место задаёт CSS (край + отступ).
  const dockStyle: CSSProperties = dragAt
    ? {
        left: dragAt.x - FAB_SIZE / 2,
        top: dragAt.y - FAB_SIZE / 2,
        right: "auto",
        bottom: "auto",
      }
    : pos
      ? ({ "--fk-fb-bottom": `${pos.bottom}px` } as CSSProperties)
      : {};

  const mineBadge: ReactNode =
    counts && counts.verify ? (
      <Pill tone="var(--fk-warning-text)">{counts.verify} на проверке</Pill>
    ) : counts && counts.unread ? (
      <Pill tone="var(--fk-orange-hover)">
        {counts.unread} {plural(counts.unread, ["ответ", "ответа", "ответов"])}
      </Pill>
    ) : counts && counts.open ? (
      <span className="fk-fb-muted">{counts.open}</span>
    ) : null;

  const panelView = view === "menu" ? null : view;
  const isForm = panelView === "bug" || panelView === "idea" || panelView === "question";

  return (
    <div
      ref={dockRef}
      className="fk-fb-dock"
      data-side={side}
      data-dragging={dragAt ? "true" : "false"}
      data-full={open && panelView && full ? "true" : "false"}
      style={dockStyle}
    >
      {open && view === "menu" ? (
        <div
          ref={menuRef}
          className="fk-fb-menu"
          role="menu"
          aria-label="Обращения"
          onKeyDown={onMenuKeyDown}
        >
          {questionOn ? (
            <Plate icon={<IconCircleHelp size={18} />} tone="var(--fk-info)" onClick={() => go("question")}>
              Вопрос в УК
            </Plate>
          ) : null}
          <Plate icon={<IconBug size={18} />} tone="var(--fk-error)" onClick={() => go("bug")}>
            Сообщить об ошибке
          </Plate>
          <Plate icon={<IconLightbulb size={18} />} tone="var(--fk-success-text)" onClick={() => go("idea")}>
            Предложить идею
          </Plate>
          <Plate
            icon={<IconClipboardList size={18} />}
            tone="var(--fk-indigo)"
            onClick={openMine}
            badge={
              <>
                {mineBadge}
                {franchisee ? <IconExternalLink size={13} /> : null}
              </>
            }
          >
            Мои обращения
          </Plate>
        </div>
      ) : null}

      {open && panelView ? (
        <>
          {full ? <div className="fk-fb-backdrop" onClick={() => setExpanded(false)} /> : null}
          <div
            className="fk-fb-panel"
            role="dialog"
            aria-label={TITLES[panelView]}
            data-full={full ? "true" : "false"}
            data-narrow={narrow ? "true" : "false"}
          >
            <div className="fk-fb-head">
              {panelView === "detail" || isForm ? (
                <IconBtn title="Назад" onClick={() => setView(panelView === "detail" ? "mine" : "menu")}>
                  <IconArrowLeft size={14} />
                </IconBtn>
              ) : null}
              <div className="fk-fb-head-text">
                <div className="fk-fb-title">{TITLES[panelView]}</div>
                {subtitle(panelView) ? <div className="fk-fb-subtitle">{subtitle(panelView)}</div> : null}
              </div>
              {!narrow ? (
                <IconBtn
                  title={expanded ? "Свернуть" : "Развернуть на весь экран"}
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? <IconMinimize size={14} /> : <IconMaximize size={14} />}
                </IconBtn>
              ) : null}
              <IconBtn title="Закрыть" onClick={close}>
                <IconX size={14} />
              </IconBtn>
            </div>
            <div className="fk-fb-body">
              {unauth && panelView !== "sent" ? (
                <div className="fk-fb-pad">
                  <CrmLoginNeeded crmUrl={CRM_URL} onRetry={retryAuth} />
                </div>
              ) : null}
              {isForm ? (
                <FeedbackForm
                  key={panelView}
                  kind={panelView}
                  roleLabel={roleLabel}
                  full={full}
                  sla={texts.slaText}
                  onAuthLost={onAuthLost}
                  onSent={(r) => {
                    setAuthLost(false);
                    setSent(r);
                    setView("sent");
                    void qc.invalidateQueries({ queryKey: ["feedback", "widget"] });
                  }}
                />
              ) : null}
              {panelView === "mine" && !unauth ? (
                <MineList
                  onOpen={(id) => {
                    setDetailId(id);
                    setView("detail");
                  }}
                  onNew={(k) => setView(k)}
                  onAuthLost={onAuthLost}
                />
              ) : null}
              {panelView === "detail" && detailId && !unauth ? (
                <MineDetail
                  id={detailId}
                  full={full}
                  onAuthLost={onAuthLost}
                  onChanged={() => void qc.invalidateQueries({ queryKey: ["feedback", "widget"] })}
                />
              ) : null}
              {panelView === "sent" && sent ? (
                <div className="fk-fb-sent">
                  {sent.question ? (
                    <>
                      <div className="fk-fb-sent-main">
                        Вопрос <b className="fk-mono">{sent.code}</b> отправлен в отдел «{sent.question.deptLabel}».{" "}
                        {sent.question.assigneeName
                          ? `Ответит ${sent.question.assigneeName}.`
                          : "Руководитель отдела назначит ответственного."}
                      </div>
                      <div className="fk-fb-sub">
                        {sentDueText(sent.question)} Ответ придёт в колокольчик CRM; статус — в «Мои обращения».
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="fk-fb-sent-main">
                        Обращение <b className="fk-mono">{sent.code}</b> отправлено — оно уже на доске команды как «Новое».
                      </div>
                      <div className="fk-fb-sub">
                        {texts.slaText}. Ответ придёт в колокольчик CRM; статус — в «Мои обращения».
                      </div>
                    </>
                  )}
                  <div className="fk-fb-sent-actions">
                    {franchisee ? (
                      <button type="button" className="fk-fb-tool fk-fb-tool--primary" onClick={openMine}>
                        Мои обращения
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="fk-fb-tool fk-fb-tool--primary"
                        onClick={() => {
                          setDetailId(sent.id);
                          setView("detail");
                        }}
                      >
                        Открыть обращение
                      </button>
                    )}
                    <button type="button" className="fk-fb-tool" onClick={close}>
                      Готово
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      <button
        ref={btnRef}
        type="button"
        className="fk-fb-fab"
        aria-label={open ? "Закрыть обращения" : "Обращения: вопрос в УК, ошибка, идея, мои обращения"}
        aria-haspopup="menu"
        aria-expanded={open}
        title={open ? "Закрыть" : "Вопрос в УК, ошибка, идея · кнопку можно перетащить, двойной клик — на место"}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            // С клавиатуры перетаскивания не бывает — клик не глушим.
            suppressClick.current = false;
            keyboardOpen.current = true;
          }
        }}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          // Новый жест — прошлый «клик после переноса» больше не глушим.
          suppressClick.current = false;
          keyboardOpen.current = false;
          drag.current = { x: e.clientX, y: e.clientY, moved: false };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < FAB_DRAG_THRESHOLD) return;
          if (!d.moved) close();
          d.moved = true;
          setDragAt({ x: e.clientX, y: e.clientY });
        }}
        onPointerUp={(e) => {
          const d = drag.current;
          drag.current = null;
          setDragAt(null);
          if (!d?.moved) return;
          suppressClick.current = true;
          const next = fabPosFromDrop({
            x: e.clientX,
            y: e.clientY,
            viewportW: window.innerWidth,
            viewportH: window.innerHeight,
            size: FAB_SIZE,
            safeBottom: safeBottom(safeProbe.current),
            topReserve: headerReserve(),
          });
          setPos(next);
          saveFabPos(next);
        }}
        onPointerCancel={() => {
          drag.current = null;
          setDragAt(null);
        }}
        onDoubleClick={() => {
          close();
          setPos(null);
          saveFabPos(null);
        }}
        onClick={() => {
          // Клик, которым закончилось перетаскивание, меню не открывает.
          if (suppressClick.current) {
            suppressClick.current = false;
            return;
          }
          if (open) close();
          else go("menu");
        }}
      >
        <span className="fk-fb-fab-ic">
          <IconPlus size={26} />
        </span>
        {!open && badge > 0 ? <span className="fk-fb-fab-badge">{badge}</span> : null}
        <span ref={safeProbe} className="fk-fb-safe" aria-hidden="true" />
      </button>
    </div>
  );
}
